import React, { useEffect, useState, useMemo, useRef } from "react";
import { useProperty } from "@/context/PropertyContext";
import api from "@/lib/api";
import { PageHeader } from "@/components/PageHeader";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription,
} from "@/components/ui/dialog";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from "@/components/ui/table";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import {
  ResponsiveContainer, BarChart, Bar, CartesianGrid, XAxis, YAxis, Tooltip, Legend, Cell, PieChart, Pie, LabelList,
  DefaultTooltipContent,
} from "recharts";
import { Plus, Trash2, ChevronLeft, ChevronRight } from "lucide-react";
import { eur, eur0, MESES_NOMBRE } from "@/lib/format";
import { toast } from "sonner";

const CONCEPTOS = [
  ["luz", "Luz"], ["gas", "Gas"], ["agua", "Agua"], ["basuras", "Basuras"],
  ["internet", "Internet"], ["comunidad", "Comunidad"], ["seguro", "Seguro"],
  ["otros", "Otros"],
];

// Mismo orden/colores fijos que Statistics.js: la identidad de cada concepto
// no cambia aunque el filtro reduzca cuantos aparecen.
const GASTO_COLORS = [
  "hsl(4 100% 60%)", "hsl(37 91% 55%)", "hsl(223 100% 50%)", "hsl(160 84% 39%)",
  "hsl(262 83% 58%)", "hsl(0 0% 60%)", "hsl(180 100% 35%)", "hsl(330 80% 50%)",
];

const chartGrid = "hsl(var(--border))";
const chartTooltip = {
  borderRadius: 8,
  border: "1px solid hsl(var(--border))",
  background: "hsl(var(--popover))",
  color: "hsl(var(--popover-foreground))",
  fontSize: 13,
};
const chartTick = { fontSize: 12, fill: "hsl(var(--muted-foreground))" };

function computeTotales(rows) {
  const t = { total: 0 };
  CONCEPTOS.forEach(([k]) => (t[k] = 0));
  rows.forEach((e) => {
    CONCEPTOS.forEach(([k]) => (t[k] += Number(e.detalle?.[k] || 0)));
    t.total += Number(e.importe || 0);
  });
  return t;
}

function computeGastoPorConcepto(totales) {
  return CONCEPTOS
    .map(([k, l], i) => ({ key: k, label: l, value: Math.round((totales[k] || 0) * 100) / 100, color: GASTO_COLORS[i] }))
    .filter((d) => d.value > 0);
}

function buildSerieMensual(rows) {
  const sorted = [...rows].sort((a, b) => {
    const ay = Number(a.anio || 0), by = Number(b.anio || 0), am = Number(a.mes || 0), bm = Number(b.mes || 0);
    return ay !== by ? ay - by : am - bm;
  });
  return sorted.map((e) => ({
    id: e.id,
    periodo: `${e.mes ? MESES_NOMBRE[e.mes - 1].slice(0, 3) : "?"} ${String(e.anio ?? "").slice(-2)}`,
    total: Math.round((e.importe || 0) * 100) / 100,
    _zero: 0, // ancla de altura 0 para la etiqueta del total (ver más abajo)
    ...Object.fromEntries(CONCEPTOS.map(([k]) => [k, Math.round((e.detalle?.[k] || 0) * 100) / 100])),
  }));
}

// Selector de año compacto y reutilizable: cada sección (tabla, evolución,
// composición, donut) mantiene su propio filtro para no obligar a hacer
// scroll hasta arriba cada vez que se quiere cambiar de año.
function YearFilter({ value, onChange, anios, testId, label = "Año" }) {
  const idx = anios.indexOf(Number(value));
  const step = (delta) => {
    if (value === "todos") return;
    const next = idx + delta;
    if (next < 0 || next >= anios.length) return;
    onChange(String(anios[next]));
  };
  return (
    <div className="flex items-center gap-1">
      <Label className="text-xs whitespace-nowrap mr-1">{label}:</Label>
      <Button variant="outline" size="sm" className="h-8 w-8 p-0" onClick={() => step(-1)} disabled={value === "todos" || idx <= 0}>
        <ChevronLeft className="h-4 w-4" />
      </Button>
      <Select value={value} onValueChange={onChange}>
        <SelectTrigger className="w-24 h-8 text-xs" data-testid={testId}><SelectValue /></SelectTrigger>
        <SelectContent>
          <SelectItem value="todos">Todos</SelectItem>
          {anios.map((y) => <SelectItem key={y} value={String(y)}>{y}</SelectItem>)}
        </SelectContent>
      </Select>
      <Button variant="outline" size="sm" className="h-8 w-8 p-0" onClick={() => step(1)} disabled={value === "todos" || idx === -1 || idx >= anios.length - 1}>
        <ChevronRight className="h-4 w-4" />
      </Button>
    </div>
  );
}

// El bar "_zero" es una ancla invisible usada solo para posicionar la
// etiqueta del total (ver más abajo); el Tooltip por defecto la listaría
// igualmente aunque no se vea en el gráfico ni en la leyenda, así que se
// filtra aquí antes de delegar en el contenido por defecto de Recharts.
function ComposicionTooltipContent(props) {
  const payload = (props.payload || []).filter((p) => p.dataKey !== "_zero");
  return <DefaultTooltipContent {...props} payload={payload} itemSorter={null} />;
}

function EditableAmountCell({ value, onCommit, testId }) {
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState("");
  const inputRef = useRef(null);

  useEffect(() => {
    if (editing) { inputRef.current?.focus(); inputRef.current?.select(); }
  }, [editing]);

  const startEdit = () => {
    setDraft(value ? String(value) : "");
    setEditing(true);
  };

  const commit = () => {
    setEditing(false);
    const raw = draft.trim();
    const num = raw === "" ? 0 : Number(raw);
    if (Number.isNaN(num) || num < 0) { toast.error("Importe inválido"); return; }
    if (num === Number(value || 0)) return;
    onCommit(num);
  };

  if (editing) {
    return (
      <input
        ref={inputRef}
        type="number"
        min="0"
        step="0.01"
        value={draft}
        onChange={(ev) => setDraft(ev.target.value)}
        onBlur={commit}
        onKeyDown={(ev) => {
          if (ev.key === "Enter") { ev.currentTarget.blur(); }
          if (ev.key === "Escape") { setEditing(false); }
        }}
        data-testid={testId}
        className="no-spinner w-full text-right font-mono text-sm bg-secondary rounded px-1.5 py-0.5 -mx-1.5 outline-none ring-1 ring-primary"
      />
    );
  }

  return (
    <button
      type="button"
      onClick={startEdit}
      data-testid={testId}
      className="w-full text-right font-mono text-sm rounded px-1.5 py-0.5 -mx-1.5 hover:bg-secondary/60 transition-colors"
    >
      {value ? eur(value) : <span className="text-muted-foreground/40">—</span>}
    </button>
  );
}

// Filas de descripción + importe para el desglose de "Otros" (sin el envoltorio
// de Dialog, así se reutiliza tanto dentro del modal por mes como en el alta
// de un mes nuevo).
function OtrosItemsFields({ items, onChange }) {
  const updateItem = (i, field, value) =>
    onChange(items.map((it, idx) => (idx === i ? { ...it, [field]: value } : it)));
  const addItem = () => onChange([...items, { descripcion: "", importe: "" }]);
  const removeItem = (i) => onChange(items.filter((_, idx) => idx !== i));
  const total = items.reduce((s, it) => s + (Number(it.importe) || 0), 0);

  return (
    <div className="space-y-2">
      {items.map((it, i) => (
        <div key={i} className="flex gap-2 items-center">
          <Input
            placeholder="Descripción"
            value={it.descripcion}
            onChange={(e) => updateItem(i, "descripcion", e.target.value)}
            className="flex-1"
            data-testid={`otros-item-descripcion-${i}`}
          />
          <Input
            type="number" min="0" step="0.01" placeholder="0,00"
            value={it.importe}
            onChange={(e) => updateItem(i, "importe", e.target.value)}
            className="w-24"
            data-testid={`otros-item-importe-${i}`}
          />
          <button
            type="button"
            onClick={() => removeItem(i)}
            className="p-1.5 text-muted-foreground hover:text-destructive shrink-0"
          >
            <Trash2 className="h-3.5 w-3.5" />
          </button>
        </div>
      ))}
      <Button type="button" variant="outline" size="sm" onClick={addItem} data-testid="otros-add-item">
        <Plus className="h-3.5 w-3.5 mr-1.5" /> Añadir línea
      </Button>
      <div className="rounded-md bg-secondary px-3 py-2 flex justify-between items-center text-sm">
        <span className="font-medium">Total otros</span>
        <span className="font-mono font-semibold">{eur(Math.round(total * 100) / 100)}</span>
      </div>
    </div>
  );
}

// Modal para editar el desglose de "Otros" de un mes ya guardado: mantiene la
// traza de qué compone ese importe en vez de un único número sin contexto.
function OtrosDesgloseDialog({ open, onOpenChange, initialItems, onSave, titulo }) {
  const [items, setItems] = useState([]);

  useEffect(() => {
    if (open) setItems(initialItems.length ? initialItems.map((it) => ({ ...it })) : [{ descripcion: "", importe: "" }]);
  }, [open, initialItems]);

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle className="font-display">{titulo}</DialogTitle>
          <DialogDescription>Detalla cada concepto para llevar la traza de qué compone este importe.</DialogDescription>
        </DialogHeader>
        <OtrosItemsFields items={items} onChange={setItems} />
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>Cancelar</Button>
          <Button onClick={() => onSave(items)} data-testid="otros-save-button">Guardar</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

// Celda de la columna "Otros": en vez de edición inline de un número suelto,
// abre el modal de desglose para poder documentar de qué se compone.
function OtrosCell({ expense, onSave, testId }) {
  const [open, setOpen] = useState(false);
  const items = expense.detalle?.otros_items || [];
  const total = expense.detalle?.otros;

  return (
    <>
      <button
        type="button"
        onClick={() => setOpen(true)}
        data-testid={testId}
        className="w-full flex items-center justify-end gap-1 text-right font-mono text-sm rounded px-1.5 py-0.5 -mx-1.5 hover:bg-secondary/60 transition-colors"
      >
        {items.length > 1 && <span className="text-[10px] text-muted-foreground">{items.length}×</span>}
        {total ? eur(total) : <span className="text-muted-foreground/40">—</span>}
      </button>
      <OtrosDesgloseDialog
        open={open}
        onOpenChange={setOpen}
        initialItems={items.length ? items : (total ? [{ descripcion: "", importe: total }] : [])}
        onSave={async (newItems) => {
          const ok = await onSave(newItems);
          if (ok !== false) setOpen(false);
        }}
        titulo={`Otros gastos — ${expense.mes ? MESES_NOMBRE[expense.mes - 1] : "—"} ${expense.anio ?? ""}`}
      />
    </>
  );
}

export default function Expenses() {
  const { selectedId } = useProperty();
  const [expenses, setExpenses] = useState([]);
  const [loading, setLoading] = useState(true);
  const [open, setOpen] = useState(false);
  const [tipo, setTipo] = useState("suministro");
  const [form, setForm] = useState({});
  const [filtroAnioTabla, setFiltroAnioTabla] = useState(() => String(new Date().getFullYear()));
  const [filtroAnioEvolucion, setFiltroAnioEvolucion] = useState(() => String(new Date().getFullYear()));
  const [filtroAnioComposicion, setFiltroAnioComposicion] = useState(() => String(new Date().getFullYear()));
  const [filtroAnioDonut, setFiltroAnioDonut] = useState(() => String(new Date().getFullYear()));
  const [filtroAnioResumen, setFiltroAnioResumen] = useState(() => String(new Date().getFullYear()));
  const [orderSuministros, setOrderSuministros] = useState("desc");
  const [conceptosOcultos, setConceptosOcultos] = useState(() => new Set());

  const toggleConcepto = (key) => {
    setConceptosOcultos((prev) => {
      const next = new Set(prev);
      if (next.has(key)) next.delete(key); else next.add(key);
      return next;
    });
  };

  const now = new Date();
  const currentYear = now.getFullYear();
  const currentMonth = now.getMonth() + 1;

  const load = () => {
    if (!selectedId) return setLoading(false);
    setLoading(true);
    api.get(`/expenses?property_id=${selectedId}`).then((r) => setExpenses(r.data)).finally(() => setLoading(false));
  };
  useEffect(load, [selectedId]);

  const openNewSuministro = () => {
    setTipo("suministro");
    setForm({ anio: new Date().getFullYear(), mes: new Date().getMonth() + 1, luz: "", gas: "", agua: "", basuras: "", internet: "", comunidad: "", seguro: "", otros_items: [] });
    setOpen(true);
  };
  const openNewUnico = () => {
    setTipo("unico");
    setForm({ concepto: "", importe: "", anio: new Date().getFullYear() });
    setOpen(true);
  };

  const saveSuministro = async () => {
    if (CONCEPTOS.some(([k]) => k !== "otros" && Number(form[k]) < 0)) return toast.error("Los importes no pueden ser negativos");
    const detalle = {};
    let total = 0;
    CONCEPTOS.forEach(([k]) => {
      if (k === "otros") return;
      const v = Number(form[k]);
      if (v) { detalle[k] = v; total += v; }
    });
    const otrosItems = (form.otros_items || [])
      .map((it) => ({ descripcion: (it.descripcion || "").trim(), importe: Math.round((Number(it.importe) || 0) * 100) / 100 }))
      .filter((it) => it.descripcion || it.importe > 0);
    const otrosTotal = Math.round(otrosItems.reduce((s, it) => s + it.importe, 0) * 100) / 100;
    if (otrosTotal > 0) { detalle.otros = otrosTotal; total += otrosTotal; }
    if (otrosItems.length) detalle.otros_items = otrosItems;
    if (total <= 0) return toast.error("Introduce al menos un importe");
    const payload = {
      property_id: selectedId, tipo: "suministro", concepto: "Suministros",
      anio: Number(form.anio), mes: Number(form.mes), importe: Math.round(total * 100) / 100, detalle,
    };
    try {
      await api.post("/expenses", payload);
      toast.success("Mes añadido");
      setOpen(false); load();
    } catch { toast.error("Error al guardar"); }
  };

  // Aplica un cambio de detalle sobre un mes ya guardado: actualiza el estado
  // local al instante (optimista) y recarga desde el servidor si la petición
  // falla, para no dejar la tabla desincronizada. Devuelve false si no se pudo
  // aplicar (para que quien llama pueda decidir no cerrar su modal, etc.).
  const commitDetalle = async (expense, updateFn) => {
    const detalle = updateFn({ ...expense.detalle });
    const total = CONCEPTOS.reduce((s, [k]) => s + Number(detalle[k] || 0), 0);
    if (total <= 0) { toast.error("Debe quedar al menos un importe en el mes"); return false; }
    const importe = Math.round(total * 100) / 100;
    setExpenses((prev) => prev.map((e) => (e.id === expense.id ? { ...e, detalle, importe } : e)));
    try {
      await api.put(`/expenses/${expense.id}`, { importe, detalle });
      return true;
    } catch {
      toast.error("Error al guardar el cambio");
      load();
      return false;
    }
  };

  // Edición directa sobre una celda simple: solo toca el concepto cambiado.
  const saveInlineCell = (expense, key, num) =>
    commitDetalle(expense, (detalle) => {
      if (num > 0) detalle[key] = num; else delete detalle[key];
      return detalle;
    });

  // Guarda el desglose de "Otros" (descripción + importe por línea) y recalcula
  // su total; conserva las líneas para poder consultar la traza más adelante.
  const saveOtrosDesglose = (expense, items) => {
    const cleaned = items
      .map((it) => ({ descripcion: (it.descripcion || "").trim(), importe: Math.round((Number(it.importe) || 0) * 100) / 100 }))
      .filter((it) => it.descripcion || it.importe > 0);
    const sum = Math.round(cleaned.reduce((s, it) => s + it.importe, 0) * 100) / 100;
    return commitDetalle(expense, (detalle) => {
      if (sum > 0) detalle.otros = sum; else delete detalle.otros;
      if (cleaned.length) detalle.otros_items = cleaned; else delete detalle.otros_items;
      return detalle;
    });
  };

  const saveUnico = async () => {
    if (!form.importe) return toast.error("Indica el importe");
    if (Number(form.importe) < 0) return toast.error("El importe no puede ser negativo");
    try {
      await api.post("/expenses", {
        property_id: selectedId, tipo: "unico", concepto: form.concepto || "Gasto",
        anio: form.anio ? Number(form.anio) : null, mes: null, importe: Number(form.importe) || 0, detalle: {},
      });
      toast.success("Gasto añadido");
      setOpen(false); load();
    } catch { toast.error("Error al guardar"); }
  };

  const remove = async (id) => {
    if (!window.confirm("¿Eliminar este gasto?")) return;
    await api.delete(`/expenses/${id}`);
    load();
  };

  const suministros = useMemo(
    () => {
      const out = expenses.filter((e) => e.tipo === "suministro");
      out.sort((a, b) => {
        const ay = Number(a.anio || 0);
        const by = Number(b.anio || 0);
        const am = Number(a.mes || 0);
        const bm = Number(b.mes || 0);
        if (ay !== by) return orderSuministros === "desc" ? by - ay : ay - by;
        return orderSuministros === "desc" ? bm - am : am - bm;
      });
      return out;
    },
    [expenses, orderSuministros]
  );
  const unicos = useMemo(() => expenses.filter((e) => e.tipo === "unico"), [expenses]);

  // Rango continuo (sin huecos) para que las flechas de año de YearFilter
  // siempre aterricen en una opción válida del desplegable. Un año futuro sí
  // puede aparecer si ya hay algún gasto real fechado ahí.
  const anios = useMemo(() => {
    const years = suministros.map((s) => Number(s.anio)).filter(Boolean);
    years.push(currentYear);
    const min = Math.min(...years);
    const max = Math.max(...years);
    const out = [];
    for (let y = min; y <= max; y++) out.push(y);
    return out;
  }, [suministros, currentYear]);
  const filtrarPorAnio = (rows, anio) => (anio === "todos" ? rows : rows.filter((s) => String(s.anio) === anio));

  // Cada sección de la pestaña de suministros (tabla, evolución, composición,
  // donut) tiene su propio filtro de año, para poder cambiarlo sin tener que
  // hacer scroll hasta el filtro de otra sección.
  const filtradosTabla = useMemo(() => filtrarPorAnio(suministros, filtroAnioTabla), [suministros, filtroAnioTabla]);
  const filtradosEvolucion = useMemo(() => filtrarPorAnio(suministros, filtroAnioEvolucion), [suministros, filtroAnioEvolucion]);
  const filtradosComposicion = useMemo(() => filtrarPorAnio(suministros, filtroAnioComposicion), [suministros, filtroAnioComposicion]);
  const filtradosDonut = useMemo(() => filtrarPorAnio(suministros, filtroAnioDonut), [suministros, filtroAnioDonut]);
  const filtradosResumen = useMemo(() => filtrarPorAnio(suministros, filtroAnioResumen), [suministros, filtroAnioResumen]);

  const totalesColTabla = useMemo(() => computeTotales(filtradosTabla), [filtradosTabla]);
  const totalesColComposicion = useMemo(() => computeTotales(filtradosComposicion), [filtradosComposicion]);
  const totalesColDonut = useMemo(() => computeTotales(filtradosDonut), [filtradosDonut]);
  const totalesColResumen = useMemo(() => computeTotales(filtradosResumen), [filtradosResumen]);

  const totalUni = unicos.reduce((s, e) => s + e.importe, 0);
  const cell = (v) => (v ? eur(v) : <span className="text-muted-foreground/40">—</span>);

  const totalSuministros = suministros.reduce((s, e) => s + e.importe, 0);

  // Series cronológicas (ascendentes) para los gráficos, cada una con su propio filtro de año.
  const serieMensual = useMemo(() => buildSerieMensual(filtradosEvolucion), [filtradosEvolucion]);
  const serieMensualBase = useMemo(() => buildSerieMensual(filtradosComposicion), [filtradosComposicion]);

  // Para el gráfico de composición: a los conceptos ocultados se les pone
  // valor 0 (así su tramo desaparece de la pila) y se recalcula el total
  // visible, para que la cifra impresa sobre la barra baje/suba al instante
  // al activar o desactivar un concepto en la leyenda.
  const serieMensualComposicion = useMemo(() => {
    return serieMensualBase.map((row) => {
      const visibles = {};
      let visibleTotal = 0;
      CONCEPTOS.forEach(([k]) => {
        const v = conceptosOcultos.has(k) ? 0 : Number(row[k] || 0);
        visibles[k] = v;
        visibleTotal += v;
      });
      return { ...row, ...visibles, _visibleTotal: Math.round(visibleTotal * 100) / 100 };
    });
  }, [serieMensualBase, conceptosOcultos]);

  const gastoPorConceptoTabla = useMemo(() => computeGastoPorConcepto(totalesColTabla), [totalesColTabla]);
  const gastoPorConceptoComposicion = useMemo(() => computeGastoPorConcepto(totalesColComposicion), [totalesColComposicion]);
  const gastoPorConceptoDonut = useMemo(() => computeGastoPorConcepto(totalesColDonut), [totalesColDonut]);

  const mediaMensual = filtradosTabla.length ? totalesColTabla.total / filtradosTabla.length : 0;
  const mesTop = filtradosTabla.reduce((max, e) => (!max || e.importe > max.importe ? e : max), null);
  const conceptoTop = gastoPorConceptoTabla.reduce((max, d) => (!max || d.value > max.value ? d : max), null);

  if (!selectedId)
    return <div className="data-card p-10 text-center text-muted-foreground">Selecciona o crea un piso primero.</div>;

  return (
    <div data-testid="expenses-page">
      <PageHeader title="Gastos" subtitle={`Suministros: ${eur(totalSuministros)} · Inversiones/generales: ${eur(totalUni)}`} />

      <Tabs defaultValue="suministros">
        <TabsList>
          <TabsTrigger value="suministros" data-testid="tab-suministros">Suministros mensuales</TabsTrigger>
          <TabsTrigger value="unicos" data-testid="tab-unicos">Inversiones / gastos generales</TabsTrigger>
        </TabsList>

        <TabsContent value="suministros">
          <div className="flex flex-wrap justify-between items-center gap-3 mb-3">
            <YearFilter value={filtroAnioTabla} onChange={setFiltroAnioTabla} anios={anios} testId="expenses-year-filter" />
            <button
              onClick={() => setOrderSuministros((o) => (o === "desc" ? "asc" : "desc"))}
              className="px-3 py-1.5 rounded-md text-sm font-medium bg-card border border-border text-muted-foreground hover:text-foreground"
              data-testid="suministros-order-toggle"
              title="Ordenar por periodo"
            >
              {orderSuministros === "desc" ? "Más nuevos primero" : "Más antiguos primero"}
            </button>
            <Button size="sm" onClick={openNewSuministro} data-testid="add-suministro-button"><Plus className="h-4 w-4 mr-1.5" /> Añadir mes</Button>
          </div>

          <div className="data-card overflow-x-auto">
            <Table data-testid="suministros-matrix" className="table-fixed min-w-[900px]">
              {/* Anchos de columna fijos: con table-layout:auto, entrar en modo edición
                  cambiaba el ancho "preferido" que el navegador calcula para un <input>
                  y desplazaba toda la tabla. Con columnas fijas eso ya no depende del
                  contenido de la celda. */}
              <colgroup>
                <col className="w-[15%]" />
                {CONCEPTOS.map(([k]) => <col key={k} className="w-[8.5%]" />)}
                <col className="w-[12%]" />
                <col className="w-[5%]" />
              </colgroup>
              <TableHeader>
                <TableRow>
                  <TableHead className="sticky left-0 bg-card">Periodo</TableHead>
                  {CONCEPTOS.map(([k, l]) => <TableHead key={k} className="text-right">{l}</TableHead>)}
                  <TableHead className="text-right font-semibold">Total</TableHead>
                  <TableHead className="w-16"></TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {loading ? (
                  <TableRow><TableCell colSpan={11} className="text-center py-8 text-muted-foreground">Cargando…</TableCell></TableRow>
                ) : filtradosTabla.length === 0 ? (
                  <TableRow><TableCell colSpan={11} className="text-center py-8 text-muted-foreground">Sin suministros</TableCell></TableRow>
                ) : (
                  filtradosTabla.map((e) => {
                    const isCurrent = Number(e.anio) === currentYear && Number(e.mes) === currentMonth;
                    return (
                    <TableRow
                      key={e.id}
                      data-testid={`suministro-row-${e.id}`}
                      className={isCurrent ? "bg-primary/10" : ""}
                    >
                      <TableCell className={`sticky left-0 font-medium whitespace-nowrap ${isCurrent ? "bg-primary/10" : "bg-card"}`}>
                        {e.mes ? MESES_NOMBRE[e.mes - 1] : "—"} <span className="text-muted-foreground font-mono text-xs">{e.anio}</span>
                      </TableCell>
                      {CONCEPTOS.map(([k]) => (
                        <TableCell key={k} className="p-1">
                          {k === "otros" ? (
                            <OtrosCell
                              expense={e}
                              onSave={(items) => saveOtrosDesglose(e, items)}
                              testId={`suministro-${e.id}-otros`}
                            />
                          ) : (
                            <EditableAmountCell
                              value={e.detalle?.[k]}
                              onCommit={(num) => saveInlineCell(e, k, num)}
                              testId={`suministro-${e.id}-${k}`}
                            />
                          )}
                        </TableCell>
                      ))}
                      <TableCell className="text-right font-mono font-semibold">{eur(e.importe)}</TableCell>
                      <TableCell>
                        <div className="flex gap-0.5">
                          <button onClick={() => remove(e.id)} className="p-1 text-muted-foreground hover:text-destructive"><Trash2 className="h-3.5 w-3.5" /></button>
                        </div>
                      </TableCell>
                    </TableRow>
                    );
                  })
                )}
              </TableBody>
              {filtradosTabla.length > 0 && (
                <tfoot>
                  <TableRow className="border-t-2 border-border bg-secondary/50">
                    <TableCell className="sticky left-0 bg-secondary/50 font-semibold">TOTAL</TableCell>
                    {CONCEPTOS.map(([k]) => <TableCell key={k} className="text-right font-mono font-semibold text-sm">{cell(Math.round(totalesColTabla[k] * 100) / 100)}</TableCell>)}
                    <TableCell className="text-right font-mono font-bold">{eur(totalesColTabla.total)}</TableCell>
                    <TableCell></TableCell>
                  </TableRow>
                </tfoot>
              )}
            </Table>
          </div>

          {/* Estadísticas de los suministros mensuales mostrados arriba (usan el filtro de la tabla) */}
          {filtradosTabla.length === 0 ? null : (
            <div className="mt-6 space-y-4">
              <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
                <div className="data-card p-4">
                  <p className="overline">Media mensual</p>
                  <p className="font-mono text-lg font-semibold mt-1.5">{eur(mediaMensual)}</p>
                </div>
                <div className="data-card p-4">
                  <p className="overline">Mes más caro</p>
                  <p className="font-mono text-lg font-semibold mt-1.5">{mesTop ? eur(mesTop.importe) : "—"}</p>
                  {mesTop && (
                    <p className="text-xs text-muted-foreground mt-0.5 truncate">
                      {mesTop.mes ? MESES_NOMBRE[mesTop.mes - 1] : "—"} {mesTop.anio}
                    </p>
                  )}
                </div>
                <div className="data-card p-4">
                  <p className="overline">Concepto principal</p>
                  <p className="font-mono text-lg font-semibold mt-1.5">{conceptoTop ? eur(conceptoTop.value) : "—"}</p>
                  {conceptoTop && <p className="text-xs text-muted-foreground mt-0.5 truncate">{conceptoTop.label}</p>}
                </div>
              </div>

              <div className="data-card p-5">
                <div className="flex flex-wrap items-center justify-between gap-2 mb-4">
                  <h3 className="font-display font-medium">Evolución del gasto mensual</h3>
                  <YearFilter value={filtroAnioEvolucion} onChange={setFiltroAnioEvolucion} anios={anios} testId="expenses-year-filter-evolucion" />
                </div>
                <ResponsiveContainer width="100%" height={260}>
                  <BarChart data={serieMensual} margin={{ top: 20, right: 8, left: 0, bottom: 0 }}>
                    <CartesianGrid strokeDasharray="3 3" vertical={false} stroke={chartGrid} />
                    <XAxis dataKey="periodo" tick={chartTick} axisLine={false} tickLine={false} />
                    <YAxis tick={{ ...chartTick, fontFamily: "JetBrains Mono" }} axisLine={false} tickLine={false} width={64} />
                    <Tooltip formatter={(v) => eur(v)} contentStyle={chartTooltip} itemStyle={{ color: "hsl(var(--popover-foreground))" }} labelStyle={{ color: "hsl(var(--popover-foreground))" }} />
                    <Bar dataKey="total" name="Total" fill="hsl(var(--primary))" radius={[3, 3, 0, 0]}>
                      <LabelList dataKey="total" position="top" formatter={eur0} style={{ fontSize: 10, fontFamily: "JetBrains Mono", fill: "hsl(var(--muted-foreground))" }} />
                    </Bar>
                  </BarChart>
                </ResponsiveContainer>
              </div>

              <div className="data-card p-5 [&_.recharts-legend-item]:cursor-pointer">
                <div className="flex flex-wrap items-center justify-between gap-2 mb-1">
                  <h3 className="font-display font-medium">Composición por concepto</h3>
                  <div className="flex items-center gap-3">
                    {conceptosOcultos.size > 0 && (
                      <button
                        onClick={() => setConceptosOcultos(new Set())}
                        className="text-xs text-primary hover:underline"
                      >
                        Mostrar todos
                      </button>
                    )}
                    <YearFilter value={filtroAnioComposicion} onChange={setFiltroAnioComposicion} anios={anios} testId="expenses-year-filter-composicion" />
                  </div>
                </div>
                <p className="text-xs text-muted-foreground -mt-2 mb-4">
                  Qué suministro pesa más en cada mes · haz clic en la leyenda para comparar por tipo
                </p>
                <ResponsiveContainer width="100%" height={300}>
                  <BarChart data={serieMensualComposicion} margin={{ top: 20, right: 8, left: 0, bottom: 0 }}>
                    <CartesianGrid strokeDasharray="3 3" vertical={false} stroke={chartGrid} />
                    <XAxis dataKey="periodo" tick={chartTick} axisLine={false} tickLine={false} />
                    <YAxis tick={{ ...chartTick, fontFamily: "JetBrains Mono" }} axisLine={false} tickLine={false} width={64} />
                    <Tooltip
                      content={<ComposicionTooltipContent />}
                      formatter={(v) => eur(v)}
                      contentStyle={chartTooltip}
                      itemStyle={{ color: "hsl(var(--popover-foreground))" }}
                      labelStyle={{ color: "hsl(var(--popover-foreground))" }}
                    />
                    {/* Recharts ordena la leyenda alfabéticamente por defecto (itemSorter:
                        "value"); itemSorter={null} la desactiva y respeta el orden fijo de
                        declaracion de los Bar, igual que el orden de colores del donut. Al
                        pasar "hide" a un Bar, su entrada de leyenda se marca "inactive" sola. */}
                    <Legend
                      wrapperStyle={{ fontSize: 12 }}
                      itemSorter={null}
                      onClick={(entry) => toggleConcepto(entry.dataKey)}
                    />
                    {gastoPorConceptoComposicion.map((g) => (
                      <Bar
                        key={g.key}
                        dataKey={g.key}
                        name={g.label}
                        stackId="total"
                        fill={g.color}
                        radius={[2, 2, 0, 0]}
                        hide={conceptosOcultos.has(g.key)}
                      />
                    ))}
                    {/* Ancla invisible (valor siempre 0) apilada por encima de todo lo demás:
                        su posición sube o baja con el total VISIBLE (recalculado al ocultar/
                        mostrar conceptos) y solo sirve para anclar la etiqueta de esa cifra. */}
                    <Bar dataKey="_zero" stackId="total" fill="transparent" legendType="none" isAnimationActive={false}>
                      <LabelList dataKey="_visibleTotal" position="top" formatter={eur0} style={{ fontSize: 10, fontFamily: "JetBrains Mono", fill: "hsl(var(--muted-foreground))" }} />
                    </Bar>
                  </BarChart>
                </ResponsiveContainer>
                <p className="text-[11px] text-muted-foreground mt-1">
                  Los conceptos en gris están ocultos; vuelve a hacer clic para mostrarlos.
                </p>
              </div>

              <div className="data-card p-5">
                <div className="flex flex-wrap items-center justify-between gap-2 mb-4">
                  <h3 className="font-display font-medium">Reparto por concepto</h3>
                  <YearFilter value={filtroAnioDonut} onChange={setFiltroAnioDonut} anios={anios} testId="expenses-year-filter-donut" />
                </div>
                {gastoPorConceptoDonut.length === 0 ? (
                  <p className="text-sm text-muted-foreground py-8 text-center">Sin datos</p>
                ) : (
                  <div className="flex flex-col sm:flex-row items-center gap-6">
                    <ResponsiveContainer width="100%" height={200} className="max-w-[220px]">
                      <PieChart>
                        <Pie data={gastoPorConceptoDonut} dataKey="value" nameKey="label" cx="50%" cy="50%" outerRadius={80} innerRadius={45}>
                          {gastoPorConceptoDonut.map((g) => <Cell key={g.key} fill={g.color} />)}
                        </Pie>
                        <Tooltip formatter={(v) => eur(v)} contentStyle={chartTooltip} itemStyle={{ color: "hsl(var(--popover-foreground))" }} labelStyle={{ color: "hsl(var(--popover-foreground))" }} />
                      </PieChart>
                    </ResponsiveContainer>
                    <div className="space-y-1.5 text-sm w-full">
                      {gastoPorConceptoDonut.map((g) => (
                        <div key={g.key} className="flex items-center justify-between gap-3">
                          <div className="flex items-center gap-2">
                            <span className="h-3 w-3 rounded-sm inline-block" style={{ backgroundColor: g.color }} />
                            <span className="text-muted-foreground truncate">{g.label}</span>
                          </div>
                          <span className="font-mono font-medium">{eur(g.value)}</span>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            </div>
          )}
        </TabsContent>

        <TabsContent value="unicos">
          <div className="flex flex-wrap justify-between items-center gap-3 mb-3">
            <YearFilter value={filtroAnioResumen} onChange={setFiltroAnioResumen} anios={anios} testId="expenses-year-filter-unicos" />
            <div className="flex items-center gap-3">
              <p className="text-sm text-muted-foreground">Total inversiones/generales: <span className="font-mono font-semibold text-foreground">{eur(totalUni)}</span></p>
              <Button size="sm" onClick={openNewUnico} data-testid="add-unico-button"><Plus className="h-4 w-4 mr-1.5" /> Añadir gasto</Button>
            </div>
          </div>

          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-3 mb-4">
            {[
              ["luz", "Luz"], ["gas", "Gas"], ["agua", "Agua"], ["internet", "Internet"], ["comunidad", "Comunidad"], ["seguro", "Seguro"],
            ].map(([k, l]) => (
              <div key={k} className="data-card p-3">
                <p className="overline">{l}</p>
                <p className="font-mono text-lg font-semibold mt-1">{eur(Math.round((totalesColResumen[k] || 0) * 100) / 100)}</p>
              </div>
            ))}
          </div>

          <div className="data-card overflow-hidden">
            <Table>
              <TableHeader><TableRow><TableHead>Concepto</TableHead><TableHead>Año</TableHead><TableHead className="text-right">Importe</TableHead><TableHead className="w-12"></TableHead></TableRow></TableHeader>
              <TableBody>
                {unicos.length === 0 ? <TableRow><TableCell colSpan={4} className="text-center py-8 text-muted-foreground">Sin gastos únicos</TableCell></TableRow>
                  : unicos.map((e) => (
                    <TableRow key={e.id}>
                      <TableCell className="font-medium">{e.concepto}</TableCell>
                      <TableCell className="font-mono">{e.anio || "—"}</TableCell>
                      <TableCell className="text-right font-mono">{eur(e.importe)}</TableCell>
                      <TableCell><button onClick={() => remove(e.id)} className="p-1.5 text-muted-foreground hover:text-destructive"><Trash2 className="h-4 w-4" /></button></TableCell>
                    </TableRow>
                  ))}
              </TableBody>
            </Table>
          </div>
        </TabsContent>
      </Tabs>

      {/* Dialog */}
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle className="font-display">
              {tipo === "suministro" ? "Añadir suministros del mes" : "Añadir gasto general / inversión"}
            </DialogTitle>
            <DialogDescription>
              {tipo === "suministro" ? "Introduce el gasto de cada suministro; el total se calcula automáticamente." : "Gastos generales o de inversión (compra, notaría, mobiliario…)."}
            </DialogDescription>
          </DialogHeader>

          {tipo === "suministro" ? (
            <div className="space-y-3">
              <div className="grid grid-cols-2 gap-3">
                <div><Label>Año</Label><Input type="number" value={form.anio || ""} onChange={(e) => setForm((f) => ({ ...f, anio: e.target.value }))} /></div>
                <div>
                  <Label>Mes</Label>
                  <Select value={String(form.mes)} onValueChange={(v) => setForm((f) => ({ ...f, mes: v }))}>
                    <SelectTrigger data-testid="suministro-mes-select"><SelectValue /></SelectTrigger>
                    <SelectContent>{MESES_NOMBRE.map((m, i) => <SelectItem key={i} value={String(i + 1)}>{m}</SelectItem>)}</SelectContent>
                  </Select>
                </div>
              </div>
              <div className="grid grid-cols-2 gap-3">
                {CONCEPTOS.filter(([k]) => k !== "otros").map(([k, l]) => (
                  <div key={k}>
                    <Label>{l} (€)</Label>
                    <Input type="number" min="0" step="0.01" value={form[k] ?? ""} onChange={(e) => setForm((f) => ({ ...f, [k]: e.target.value }))} data-testid={`suministro-${k}-input`} />
                  </div>
                ))}
              </div>
              <div>
                <Label>Otros</Label>
                <OtrosItemsFields items={form.otros_items || []} onChange={(items) => setForm((f) => ({ ...f, otros_items: items }))} />
              </div>
              <div className="rounded-md bg-secondary p-3 flex justify-between items-center">
                <span className="text-sm font-medium">Total del mes</span>
                <span className="font-mono font-semibold">
                  {eur(
                    CONCEPTOS.filter(([k]) => k !== "otros").reduce((s, [k]) => s + (Number(form[k]) || 0), 0)
                    + (form.otros_items || []).reduce((s, it) => s + (Number(it.importe) || 0), 0)
                  )}
                </span>
              </div>
            </div>
          ) : (
            <div className="space-y-3">
              <div><Label>Concepto</Label><Input value={form.concepto || ""} onChange={(e) => setForm((f) => ({ ...f, concepto: e.target.value }))} data-testid="expense-concepto-input" placeholder="Ej: Notaría, Mobiliario…" /></div>
              <div className="grid grid-cols-2 gap-3">
                <div><Label>Año</Label><Input type="number" value={form.anio || ""} onChange={(e) => setForm((f) => ({ ...f, anio: e.target.value }))} /></div>
                <div><Label>Importe (€) *</Label><Input type="number" min="0" value={form.importe || ""} onChange={(e) => setForm((f) => ({ ...f, importe: e.target.value }))} data-testid="expense-importe-input" /></div>
              </div>
            </div>
          )}

          <DialogFooter>
            <Button variant="outline" onClick={() => setOpen(false)}>Cancelar</Button>
            <Button onClick={tipo === "suministro" ? saveSuministro : saveUnico} data-testid="save-expense-button">Guardar</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
